<template>
  <div class="blocks-library" :class="{ collapsed: isCollapsed }">
    <div class="library-header">
      <h2>Blocs</h2>
      <div class="header-actions">
        <button @click="isCollapsed = !isCollapsed" class="btn-collapse" :title="isCollapsed ? 'Afficher' : 'Masquer'">
          <span class="material-symbols-outlined">{{ isCollapsed ? 'chevron_right' : 'chevron_left' }}</span>
        </button>
        <button v-if="!isCollapsed" @click="showImportModal = true" class="btn-add">+ Ajouter</button>
      </div>
    </div>

    <div v-if="!isCollapsed" class="library-content">
      <div class="search-bar library-toolbar">
        <div class="toolbar-actions">
          <button
            class="toolbar-action-btn toolbar-search-btn"
            :class="{ active: showSearch }"
            :data-tooltip="showSearch ? 'Masquer la recherche' : 'Afficher la recherche'"
            @click="showSearch = !showSearch"
          >
            <span class="material-symbols-outlined">search</span>
          </button>

          <button
            class="toolbar-action-btn toolbar-filter-btn"
            :data-tooltip="canvasFilterTooltip"
            @click="toggleCanvasFilter"
          >
            <span class="material-symbols-outlined">{{ canvasFilterIcon }}</span>
            <span class="toolbar-chip">{{ canvasFilterLabel }}</span>
          </button>

          <button
            class="toolbar-action-btn toolbar-kind-filter-btn"
            :data-tooltip="blockKindFilterTooltip"
            @click="toggleBlockKindFilter"
          >
            <span class="material-symbols-outlined">{{ blockKindFilterIcon }}</span>
            <span class="toolbar-chip">{{ blockKindFilterLabel }}</span>
          </button>

          <button
            class="toolbar-action-btn toolbar-sort-by-btn"
            :data-tooltip="sortByTooltip"
            @click="toggleSortBy"
          >
            <span class="material-symbols-outlined">{{ sortByIcon }}</span>
          </button>

          <button
            class="toolbar-action-btn toolbar-order-btn"
            :data-tooltip="sortOrderTooltip"
            @click="toggleSortOrder"
          >
            <span class="material-symbols-outlined">{{ sortOrderIcon }}</span>
          </button>
        </div>

        <label v-if="showSearch" class="toolbar-search" for="library-search">
          <span class="material-symbols-outlined toolbar-search-icon">search</span>
          <input
            id="library-search"
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher..."
            class="search-input"
          />
        </label>
      </div>

      <div v-if="loading" class="loading">Chargement...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else class="blocks-list" ref="blocksListRef">
      <div
        v-for="bloc in filteredBlocs"
        :key="bloc.id"
        :ref="(el) => setBlocCardRef(bloc.id, el as HTMLElement)"
        :class="{
          'highlighted': selectedBloc?.id === bloc.id,
          'highlight-animate': lastHighlightedBlocId === bloc.id
        }"
      >
        <BlockCard
          :bloc="bloc"
          :selected="selectedBloc?.id === bloc.id"
          :is-in-canvas="blocsInCanvas.has(bloc.id)"
          :search-highlight="searchQuery.trim() || undefined"
          @select="handleSelectBloc"
          @edit="handleEditBloc"
          @create-avatar="handleOpenAvatarModal"
        />
      </div>
    </div>
    </div>

    <BlockImportModal
      v-if="showImportModal"
      @close="showImportModal = false"
      @created="handleBlocCreated"
    />

    <BlockEditModal
      v-if="showEditModal"
      :show="showEditModal"
      :bloc="editingBloc"
      @close="showEditModal = false; editingBloc = null"
      @updated="handleBlocUpdated"
    />

    <BlockAvatarModal
      v-if="showAvatarModal"
      :show="showAvatarModal"
      :bloc="avatarSourceBloc"
      @close="showAvatarModal = false; avatarSourceBloc = null"
      @created="handleAvatarCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import type { Bloc } from '@api/blocs.api';
import { useBlocsStore } from '@stores/blocs.store';
import { useHypothesesStore } from '@stores/hypotheses.store';
import BlockCard from './BlockCard.vue';
import BlockImportModal from './BlockImportModal.vue';
import BlockEditModal from './BlockEditModal.vue';
import BlockAvatarModal from './BlockAvatarModal.vue';

const blocsStore = useBlocsStore();
const hypothesesStore = useHypothesesStore();
const searchQuery = ref('');
const showImportModal = ref(false);
const isCollapsed = ref(true);
const blocksListRef = ref<HTMLElement | null>(null);
const blocCardRefs = ref<Map<number, HTMLElement>>(new Map());
const lastHighlightedBlocId = ref<number | null>(null);
let highlightTimeout: ReturnType<typeof setTimeout> | null = null;
let searchDebounce: ReturnType<typeof setTimeout> | null = null;
const SEARCH_DEBOUNCE_MS = 300;
const showSearch = ref(false);
const canvasFilter = ref<'all' | 'in-canvas' | 'not-in-canvas'>('all');
const blockKindFilter = ref<'all' | 'bloc' | 'avatar'>('all');
const sortBy = ref<'name' | 'size'>('name');
const sortOrder = ref<'asc' | 'desc'>('asc');

const loading = computed(() => blocsStore.loading);
const error = computed(() => blocsStore.error);
const blocs = computed(() => blocsStore.blocs);
const selectedBloc = computed(() => blocsStore.selectedBloc);
const currentHypothesis = computed(() => hypothesesStore.currentHypothesis);

// IDs des blocs déjà présents dans le canvas
const blocsInCanvas = computed(() => {
  if (!currentHypothesis.value) return new Set<number>();
  return new Set(currentHypothesis.value.blocks.map(b => b.bloc_id || b.bloc?.id).filter(Boolean));
});

function getBlocImageArea(bloc: Bloc): number {
  const width = bloc.image_width ?? 0;
  const height = bloc.image_height ?? 0;
  return width * height;
}

function getBlocKind(bloc: Bloc): 'bloc' | 'avatar' {
  return bloc.bloc_kind === 'avatar' ? 'avatar' : 'bloc';
}

// Résultats backend (recherche texte) + filtre/tri côté client
const filteredBlocs = computed(() => {
  const filteredByKind = blocs.value.filter((bloc) => {
    if (blockKindFilter.value === 'all') return true;
    return getBlocKind(bloc) === blockKindFilter.value;
  });

  const filteredByPresence = filteredByKind.filter((bloc) => {
    if (canvasFilter.value === 'in-canvas') return blocsInCanvas.value.has(bloc.id);
    if (canvasFilter.value === 'not-in-canvas') return !blocsInCanvas.value.has(bloc.id);
    return true;
  });

  return [...filteredByPresence].sort((a, b) => {
    if (sortBy.value === 'size') {
      const sizeDiff = getBlocImageArea(a) - getBlocImageArea(b);
      if (sizeDiff !== 0) return sortOrder.value === 'asc' ? sizeDiff : -sizeDiff;
    } else {
      const nameDiff = (a.object_name ?? '').localeCompare(b.object_name ?? '', 'fr', {
        sensitivity: 'base',
      });
      if (nameDiff !== 0) return sortOrder.value === 'asc' ? nameDiff : -nameDiff;
    }

    return (a.object_name ?? '').localeCompare(b.object_name ?? '', 'fr', {
      sensitivity: 'base',
    });
  });
});

const canvasFilterIcon = computed(() => {
  if (canvasFilter.value === 'in-canvas') return 'check_circle';
  if (canvasFilter.value === 'not-in-canvas') return 'radio_button_unchecked';
  return 'filter_alt';
});

const canvasFilterLabel = computed(() => {
  if (canvasFilter.value === 'in-canvas') return 'En place';
  if (canvasFilter.value === 'not-in-canvas') return 'Absents';
  return 'Tous';
});

const canvasFilterTooltip = computed(() => {
  if (canvasFilter.value === 'in-canvas') return 'Filtre: deja sur le canvas';
  if (canvasFilter.value === 'not-in-canvas') return 'Filtre: pas encore sur le canvas';
  return 'Filtre: tous les blocs';
});

const sortByIcon = computed(() => (sortBy.value === 'size' ? 'straighten' : 'sort_by_alpha'));
const sortByTooltip = computed(() => (sortBy.value === 'size' ? 'Tri par taille image' : 'Tri par nom'));

const sortOrderIcon = computed(() => (sortOrder.value === 'asc' ? 'arrow_upward' : 'arrow_downward'));
const sortOrderTooltip = computed(() => (sortOrder.value === 'asc' ? 'Ordre croissant' : 'Ordre decroissant'));

const blockKindFilterIcon = computed(() => {
  if (blockKindFilter.value === 'bloc') return 'category';
  if (blockKindFilter.value === 'avatar') return 'palette';
  return 'layers';
});

const blockKindFilterLabel = computed(() => {
  if (blockKindFilter.value === 'bloc') return 'Blocs';
  if (blockKindFilter.value === 'avatar') return 'Avatars';
  return 'Tous';
});

const blockKindFilterTooltip = computed(() => {
  if (blockKindFilter.value === 'bloc') return 'Affichage: blocs uniquement';
  if (blockKindFilter.value === 'avatar') return 'Affichage: avatars uniquement';
  return 'Affichage: blocs et avatars';
});

function toggleCanvasFilter(): void {
  if (canvasFilter.value === 'all') {
    canvasFilter.value = 'in-canvas';
    return;
  }
  if (canvasFilter.value === 'in-canvas') {
    canvasFilter.value = 'not-in-canvas';
    return;
  }
  canvasFilter.value = 'all';
}

function toggleSortBy(): void {
  sortBy.value = sortBy.value === 'name' ? 'size' : 'name';
}

function toggleBlockKindFilter(): void {
  if (blockKindFilter.value === 'all') {
    blockKindFilter.value = 'bloc';
    return;
  }
  if (blockKindFilter.value === 'bloc') {
    blockKindFilter.value = 'avatar';
    return;
  }
  blockKindFilter.value = 'all';
}

function toggleSortOrder(): void {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
}

function runSearch() {
  const q = searchQuery.value.trim() || undefined;
  blocsStore.fetchAll(q);
}

watch(searchQuery, () => {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    runSearch();
    searchDebounce = null;
  }, SEARCH_DEBOUNCE_MS);
});

const handleSelectBloc = (bloc: any) => {
  blocsStore.setSelectedBloc(bloc);
  // Déclencher l'événement pour cadrer le bloc dans le canvas
  const event = new CustomEvent('frame-block-in-canvas', {
    detail: { blocId: bloc.id },
  });
  window.dispatchEvent(event);
};

const showEditModal = ref(false);
const editingBloc = ref<any>(null);
const showAvatarModal = ref(false);
const avatarSourceBloc = ref<Bloc | null>(null);

const handleEditBloc = (bloc: any) => {
  editingBloc.value = bloc;
  showEditModal.value = true;
};

const handleOpenAvatarModal = (bloc: Bloc) => {
  avatarSourceBloc.value = bloc;
  showAvatarModal.value = true;
};

const handleBlocUpdated = () => {
  runSearch();
};

const handleAvatarCreated = () => {
  showAvatarModal.value = false;
  avatarSourceBloc.value = null;
  runSearch();
};

const handleBlocCreated = () => {
  showImportModal.value = false;
  runSearch();
};

const handleOpenImport = () => {
  showImportModal.value = true;
};

const setBlocCardRef = (blocId: number, el: HTMLElement | null) => {
  if (el) {
    blocCardRefs.value.set(blocId, el);
  } else {
    blocCardRefs.value.delete(blocId);
  }
};

const scrollToBloc = (blocId: number) => {
  nextTick(() => {
    const cardElement = blocCardRefs.value.get(blocId);
    if (cardElement && blocksListRef.value) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  });
};

const handleHighlightBloc = (event: CustomEvent) => {
  const { blocId } = event.detail;
  if (highlightTimeout) clearTimeout(highlightTimeout);
  lastHighlightedBlocId.value = blocId;
  scrollToBloc(blocId);
  highlightTimeout = setTimeout(() => {
    lastHighlightedBlocId.value = null;
    highlightTimeout = null;
  }, 600);
};

onMounted(() => {
  runSearch();
  window.addEventListener('open-import-modal', handleOpenImport);
  window.addEventListener('highlight-bloc-in-library', handleHighlightBloc as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('open-import-modal', handleOpenImport);
  window.removeEventListener('highlight-bloc-in-library', handleHighlightBloc as EventListener);
  if (highlightTimeout) clearTimeout(highlightTimeout);
  if (searchDebounce) clearTimeout(searchDebounce);
});
</script>

<style scoped>
.blocks-library {
  width: 300px;
  min-width: 300px;
  height: 100%;
  background-color: #2a2a2a;
  border-right: 1px solid #444;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.3s ease, width 0.3s ease, min-width 0.3s ease;
}

.blocks-library.collapsed {
  width: 48px;
  min-width: 48px;
  height: 100%;
  flex-direction: row;
  position: relative;
}

.library-header {
  padding: 1rem;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.blocks-library.collapsed .library-header {
  border-bottom: none;
  border-right: 1px solid #444;
  padding: 1rem 0.5rem;
  padding-top: 0;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  height: calc(100% - 60px);
  margin-top: 60px;
  cursor: pointer;
}

.blocks-library.collapsed .library-header h2 {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
  margin: 0;
  margin-top: 3rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.blocks-library.collapsed .header-actions {
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
}

.btn-collapse {
  background: #333;
  border: none;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-collapse .material-icons,
.btn-collapse .material-symbols-outlined {
  font-size: 20px;
}

.btn-collapse:hover {
  background-color: #444;
  color: #fff;
}

.blocks-library.collapsed .btn-add {
  display: none;
}

.library-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.2s ease;
  max-height: 1000px;
  opacity: 1;
}

.blocks-library.collapsed .library-content {
  display: none;
}

.library-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #e0e0e0;
}

.btn-add {
  padding: 0.5rem 1rem;
  background-color: #4a9eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-add:hover {
  background-color: #3a8eef;
}

.search-bar {
  padding: 1rem;
  border-bottom: 1px solid #444;
  position: relative;
  z-index: 20;
  overflow: visible;
}

.library-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.toolbar-action-btn {
  width: 34px;
  height: 34px;
  padding: 0 0.4rem;
  border-radius: 6px;
  border: 1px solid #444;
  background-color: #2f2f2f;
  color: #d9d9d9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  flex-shrink: 0;
  position: relative;
}

.toolbar-action-btn:hover {
  background-color: #3a3a3a;
  border-color: #575757;
}

.toolbar-action-btn.active {
  border-color: #4a9eff;
  color: #4a9eff;
}

.toolbar-action-btn .material-symbols-outlined {
  font-size: 18px;
}

.toolbar-search-btn,
.toolbar-order-btn {
  width: 30px;
  min-width: 30px;
  padding: 0;
  gap: 0;
}

.toolbar-search-btn .material-symbols-outlined,
.toolbar-order-btn .material-symbols-outlined {
  font-size: 17px;
}

.toolbar-filter-btn,
.toolbar-kind-filter-btn,
.toolbar-sort-by-btn {
  min-width: 36px;
}

.toolbar-filter-btn,
.toolbar-kind-filter-btn {
  width: auto;
  min-width: 68px;
}

.toolbar-chip {
  font-size: 0.7rem;
  line-height: 1;
  color: inherit;
}

.toolbar-action-btn[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  top: calc(100% + 6px);
  transform: translateX(-50%);
  background: #161616;
  color: #f0f0f0;
  border: 1px solid #4f4f4f;
  border-radius: 4px;
  padding: 0.22rem 0.4rem;
  font-size: 0.7rem;
  white-space: nowrap;
  pointer-events: none;
  z-index: 4000;
}

.toolbar-search {
  position: relative;
  display: block;
}

.toolbar-search-icon {
  position: absolute;
  left: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #9a9a9a;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.search-input:focus {
  outline: none;
  border-color: #4a9eff;
}


.loading,
.error {
  padding: 1rem;
  text-align: center;
  color: #e0e0e0;
}

.error {
  color: #ff6b6b;
}

.blocks-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.blocks-list > div.highlighted {
  background-color: rgba(74, 158, 255, 0.15);
}

.blocks-list > div.highlight-animate {
  animation: highlight 0.6s ease;
}

@keyframes highlight {
  0% {
    background-color: rgba(74, 158, 255, 0.4);
    box-shadow: 0 0 0 2px rgba(74, 158, 255, 0.5);
  }
  70% {
    background-color: rgba(74, 158, 255, 0.2);
    box-shadow: 0 0 0 1px rgba(74, 158, 255, 0.3);
  }
  100% {
    background-color: transparent;
    box-shadow: none;
  }
}
</style>
